import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import apiClient from "@/api/apiClient";

// Transform unavailability data from backend to frontend format
const transformUnavailabilityToFrontend = (unavailability) => ({
  id: unavailability.id,
  doctor_id: unavailability.doctorId,
  room_id: unavailability.roomId,
  equipment_id: unavailability.equipmentId,
  start_time: unavailability.startTime,
  end_time: unavailability.endTime,
  // records store startDate/endDate; legacy ones may carry `date`
  date: unavailability.date || unavailability.startDate,
  end_date: unavailability.endDate,
  reason: unavailability.reason,
  type: unavailability.type || "doctor",
  created_at: unavailability.createdAt,
  updated_at: unavailability.updatedAt,
});

// Transform unavailability data from frontend to backend format
const transformUnavailabilityToBackend = (unavailability) => ({
  doctorId: unavailability.doctor_id,
  roomId: unavailability.room_id,
  equipmentId: unavailability.equipment_id,
  startTime: unavailability.start_time,
  endTime: unavailability.end_time,
  date: unavailability.date,
  reason: unavailability.reason,
  type: unavailability.type || "doctor",
});

// Query Keys
export const unavailabilityKeys = {
  all: ["unavailability"],
  list: (filters) => ["unavailability", "list", filters],
  detail: (id) => ["unavailability", "detail", id],
  byDoctor: (doctorId) => ["unavailability", "doctor", doctorId],
  byRoom: (roomId) => ["unavailability", "room", roomId],
  byDate: (date) => ["unavailability", "date", date],
};

// Get All Unavailability Records Hook
export const useUnavailability = (filters = {}) => {
  return useQuery({
    queryKey: unavailabilityKeys.list(filters),
    queryFn: async () => {
      const response = await apiClient.get("/api/unavailability", filters);
      return response.map(transformUnavailabilityToFrontend);
    },
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
};

// Get Unavailability by ID Hook
export const useUnavailabilityRecord = (recordId) => {
  return useQuery({
    queryKey: unavailabilityKeys.detail(recordId),
    queryFn: async () => {
      const response = await apiClient.get(`/api/unavailability/${recordId}`);
      return transformUnavailabilityToFrontend(response);
    },
    enabled: !!recordId,
    staleTime: 5 * 60 * 1000,
  });
};

// Get Doctor Unavailability Hook
export const useDoctorUnavailability = (doctorId, filters = {}) => {
  return useQuery({
    queryKey: unavailabilityKeys.byDoctor(doctorId),
    queryFn: async () => {
      const response = await apiClient.get(
        `/api/unavailability/doctor/${doctorId}`,
        filters
      );
      return response.map(transformUnavailabilityToFrontend);
    },
    enabled: !!doctorId,
    staleTime: 5 * 60 * 1000,
  });
};

// Get Room Unavailability Hook
export const useRoomUnavailability = (roomId, filters = {}) => {
  return useQuery({
    queryKey: unavailabilityKeys.byRoom(roomId),
    queryFn: async () => {
      const response = await apiClient.get(
        `/api/unavailability/room/${roomId}`,
        filters
      );
      return response.map(transformUnavailabilityToFrontend);
    },
    enabled: !!roomId,
    staleTime: 5 * 60 * 1000,
  });
};

// Get Unavailability by Date Hook
export const useUnavailabilityByDate = (date) => {
  return useQuery({
    queryKey: unavailabilityKeys.byDate(date),
    queryFn: async () => {
      const response = await apiClient.get(`/api/unavailability/date/${date}`);
      return response.map(transformUnavailabilityToFrontend);
    },
    enabled: !!date,
    staleTime: 2 * 60 * 1000, // 2 minutes - availability changes frequently
  });
};

// Create Unavailability Mutation
export const useCreateUnavailability = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (unavailabilityData) => {
      const transformedData =
        transformUnavailabilityToBackend(unavailabilityData);
      const response = await apiClient.post(
        "/api/unavailability",
        transformedData
      );
      return transformUnavailabilityToFrontend(response);
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: unavailabilityKeys.all });

      // Invalidate specific resource queries
      if (variables.doctorId) {
        queryClient.invalidateQueries({
          queryKey: unavailabilityKeys.byDoctor(variables.doctorId),
        });
      }
      if (variables.roomId) {
        queryClient.invalidateQueries({
          queryKey: unavailabilityKeys.byRoom(variables.roomId),
        });
      }
      if (variables.date) {
        queryClient.invalidateQueries({
          queryKey: unavailabilityKeys.byDate(variables.date),
        });
      }

      // Also invalidate booking-related queries since availability affects slots
      queryClient.invalidateQueries({ queryKey: ["bookings", "slots"] });
      queryClient.invalidateQueries({
        queryKey: ["bookings", "available-doctors"],
      });
    },
  });
};

// Update Unavailability Mutation
export const useUpdateUnavailability = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ recordId, unavailabilityData }) => {
      const transformedData =
        transformUnavailabilityToBackend(unavailabilityData);
      const response = await apiClient.put(
        `/api/unavailability/${recordId}`,
        transformedData
      );
      return transformUnavailabilityToFrontend(response);
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: unavailabilityKeys.all });
      queryClient.invalidateQueries({
        queryKey: unavailabilityKeys.detail(variables.recordId),
      });

      // Invalidate booking-related queries
      queryClient.invalidateQueries({ queryKey: ["bookings", "slots"] });
      queryClient.invalidateQueries({
        queryKey: ["bookings", "available-doctors"],
      });
    },
  });
};

// Delete Unavailability Mutation
export const useDeleteUnavailability = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (recordId) => {
      return await apiClient.delete(`/api/unavailability/${recordId}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: unavailabilityKeys.all });

      // Invalidate booking-related queries
      queryClient.invalidateQueries({ queryKey: ["bookings", "slots"] });
      queryClient.invalidateQueries({
        queryKey: ["bookings", "available-doctors"],
      });
    },
  });
};
